import PropTypes from "prop-types";
import React, { useRef, useState } from "react";

import styled from "styled-components/macro";

import API from "Signups/API";
import Dropdown from "Signups/FamilyRegistration/Shared/Dropdown";
import { useIsSignupClosed } from "Signups/SharedComponents/useIsSignupClosed";
import { getFundItAmountsInCents } from "Signups/SignupView/SignupSheet/utils/fundItAmountsConversions";
import { useSignups } from "Signups/state.js";

import FnrButton from "UI/FnrButton3";
import { FnrPopover } from "UI/FnrPopover2";

import { fundItStateHooks } from "../stateHooks";

FundButton.propTypes = {
  currentFund: PropTypes.object,
};

export default function FundButton({ currentFund }) {
  const [
    {
      currentSignupList: { ID: signupListID },
      funds,
    },
    dispatch,
  ] = useSignups();
  const buttonRef = useRef();
  const [loadingID, setLoadingID] = useState(null);
  const [showWarningMsg, setShowWarningMsg] = useState(false);

  const commonPostData = fundItStateHooks.useCommonFundItPostData();
  const amountsInDollars = fundItStateHooks.useAmountsInDollars();

  const { isArchived } = useIsSignupClosed();

  // sorts selected fund to the beginning, then by fundID ascending
  const orderedFunds = funds?.slice()?.sort((a, b) => {
    const aSelected = a.fundID === currentFund.ID;
    const bSelected = b.fundID === currentFund.ID;
    if (aSelected) return -1;
    if (bSelected) return 1;

    return a.fundID - b.fundID;
  });

  const originalFundHasSignupPaymentsCollected =
    currentFund.signupPaymentsCollected;

  const handleFundSelect = (fundID) => {
    if (loadingID) return;

    setLoadingID(fundID);

    const amounts = getFundItAmountsInCents(amountsInDollars);

    API.editFundItOptions(signupListID, {
      ...commonPostData,
      amounts,
      signupListID,
      fundID,
    })
      .post()
      .then(({ fund: savedFund }) => {
        const fundTransformedForSignup = {
          ...savedFund,
          name: savedFund.description,
        };

        dispatch({
          type: "setSignupFund",
          payload: fundTransformedForSignup,
        });
        setTimeout(() => {
          setShowWarningMsg(originalFundHasSignupPaymentsCollected);
        }, 1000);
      })
      .catch(() => {
        alert(
          "Oh no! There was a problem updating the fund for this Signup. Please try again, or contact help@flocknote.com."
        );
      })
      .finally(() => {
        setLoadingID(null);
      });
  };

  return (
    <Wrapper ref={buttonRef}>
      <Dropdown
        nameKey="fundName"
        idKey="fundID"
        placeholder={"Select a fund"}
        options={orderedFunds}
        onSelect={handleFundSelect}
        initialSelectedId={currentFund.ID}
        disabled={isArchived}
      />

      <FnrPopover
        show={showWarningMsg}
        onShouldClose={() => setShowWarningMsg(false)}
        buttonRef={buttonRef}
      >
        <WarningWrapper onClick={(e) => e.stopPropagation()}>
          Heads up! Signup payments that have already been collected will not be
          updated. Only new payments will be directed to the newly selected
          fund.
          <WarningButtonWrapper>
            <FnrButton onClick={() => setShowWarningMsg(false)} color="blue">
              Got it!
            </FnrButton>
          </WarningButtonWrapper>
        </WarningWrapper>
      </FnrPopover>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  max-width: 360px;
`;

const WarningWrapper = styled.div`
  padding: 10px 12px;
  max-width: 300px;
`;

const WarningButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
`;
